<template>
  <header class="panel-topbar">
    <div class="topbar-inner">
      <button ref="menuButton" class="topbar-button" type="button" @click="openMenu"
        :aria-label="t('sidebar.openMenu', 'Open menu')" aria-haspopup="dialog">
        <vue-feather type="menu" size="19" />
        <span class="desktop-label">{{ t('sidebar.menu', 'Menu') }}</span>
      </button>
      <router-link class="topbar-button topbar-shop" to="/shop">
        <vue-feather type="arrow-left" size="18" />
        <span class="desktop-label">{{ t('sidebar.backToShop', 'Shop') }}</span>
      </router-link>
      <router-link class="panel-logo" to="/dashboard" aria-label="BEST GROUP">BEST GROUP</router-link>
      <div ref="orderSearch" class="global-order-search" :class="{ 'is-open': orderSearchOpen }">
        <vue-feather type="search" size="17" />
        <input v-model.trim="orderSearchQuery" type="search"
          :placeholder="t('sidebar.orderSearch.placeholder', 'Szukaj zamówienia…')"
          :aria-label="t('sidebar.orderSearch.placeholder', 'Szukaj zamówienia')"
          @input="searchOrders" @focus="openOrderSearch" @keydown.esc="closeOrderSearch" />
        <span v-if="orderSearchLoading" class="global-search-spinner" />
        <div v-if="orderSearchOpen" class="global-search-results">
          <div v-if="orderSearchLoading && !orderSearchResults.length" class="global-search-empty">{{ t('sidebar.orderSearch.loading', 'Szukam…') }}</div>
          <button v-for="result in orderSearchResults" :key="`${result.type}-${result.id}`" type="button" @click.stop="goToOrder(result)">
            <span class="global-search-icon"><vue-feather :type="result.type === 'PENDING' ? 'clock' : 'shopping-bag'" size="15" /></span>
            <span><strong>{{ result.confirmationNumber || result.shoperOrderId || `#${result.id}` }}</strong><small>{{ orderSearchDescription(result) }}</small></span>
            <vue-feather type="arrow-right" size="15" />
          </button>
          <div v-if="!orderSearchLoading && orderSearchQuery.length >= 2 && !orderSearchResults.length" class="global-search-empty">{{ t('sidebar.orderSearch.empty', 'Nie znaleziono zamówień') }}</div>
          <div v-if="orderSearchQuery.length < 2" class="global-search-empty">{{ t('sidebar.orderSearch.hint', 'Wpisz co najmniej 2 znaki') }}</div>
        </div>
      </div>
      <div class="topbar-actions">
        <span v-if="shopCurrency" class="currency-label">{{ shopCurrency }}</span>
        <div ref="languagePicker" class="topbar-language-picker">
          <button class="topbar-language" type="button" :disabled="localesLoading"
            :aria-label="t('sidebar.language', 'Language')" aria-haspopup="listbox"
            :aria-expanded="languageMenuOpen ? 'true' : 'false'" @click="toggleLanguageMenu">
            <vue-feather type="globe" size="16" />
            <span>{{ selectedLanguageName }}</span>
            <vue-feather class="select-chevron" type="chevron-down" size="15" />
          </button>
          <div v-if="languageMenuOpen" class="language-menu" role="listbox"
            :aria-label="t('sidebar.language', 'Language')">
            <button v-for="option in orderedLocaleOptions" :key="option.value" type="button"
              :class="{ selected: option.value === selectedLocale }" role="option"
              :aria-selected="option.value === selectedLocale ? 'true' : 'false'"
              @click="selectLocale(option.value)">
              <span class="language-code">{{ languageCode(option.value) }}</span>
              <span>{{ languageDisplayName(option.value) }}</span>
            </button>
          </div>
        </div>
        <router-link class="topbar-button order-button" to="/products/client">
          <vue-feather type="list" size="18" />
          <span class="desktop-label">{{ t('sidebar.newOrderShort', 'Order') }}</span>
        </router-link>
        <div v-if="advisor" class="topbar-advisor">
          <span class="advisor-avatar">{{ advisorInitial }}</span>
          <div class="advisor-copy">
            <small>{{ t('partnerDashboard.advisor.label', 'Your advisor') }}</small>
            <strong>{{ advisor.name }}</strong>
          </div>
          <a v-if="advisor.phone" class="advisor-contact advisor-phone" :href="phoneHref"
            :aria-label="advisor.phone">
            <vue-feather type="phone" size="15" /> <span>{{ advisor.phone }}</span>
          </a>
          <a v-if="advisor.email" class="advisor-contact advisor-email" :href="`mailto:${advisor.email}`"
            :aria-label="advisor.email"><vue-feather type="mail" size="16" /></a>
        </div>
        <router-link v-if="canUseCart" class="cart-button" to="/admin/cart"
          :aria-label="t('sidebar.cart', 'Cart')">
          <vue-feather type="shopping-cart" size="19" />
          <span v-if="cartCount > 0" class="topbar-badge">{{ compactCount(cartCount) }}</span>
        </router-link>
      </div>
    </div>
  </header>

  <teleport to="body">
    <div v-if="menuOpen" class="menu-layer" role="presentation">
      <div class="menu-backdrop" @click="closeMenu" />
      <aside ref="menuPanel" class="menu-panel" role="dialog" aria-modal="true"
        :aria-label="t('sidebar.menu', 'Menu')" @keydown="onPanelKeydown">
        <header class="menu-header">
          <div>
            <div class="menu-logo">BEST GROUP</div>
            <div class="menu-subtitle">{{ t('sidebar.title', 'B2B Catalog') }}</div>
          </div>
          <button ref="closeButton" class="icon-button" type="button" @click="closeMenu"
            :aria-label="t('sidebar.closeMenu', 'Close menu')">
            <vue-feather type="x" size="20" />
          </button>
        </header>

        <div class="menu-scroll">
          <router-link class="primary-action" to="/products/client" @click="closeMenu">
            <vue-feather type="plus" size="18" /> {{ t('sidebar.newOrder', 'New order') }}
          </router-link>
          <router-link class="secondary-action" to="/shop" @click="closeMenu">
            <vue-feather type="arrow-left" size="17" /> {{ t('sidebar.backToShop', 'Back to shop') }}
          </router-link>

          <nav class="menu-nav">
            <div class="menu-group-label">{{ t('sidebar.groups.main', 'Main') }}</div>
            <router-link class="menu-link" to="/dashboard" @click="closeMenu">
              <vue-feather type="grid" /> <span>{{ t('sidebar.dashboard', 'Dashboard') }}</span>
            </router-link>

            <div class="menu-group-label">{{ t('sidebar.groups.catalog', 'Catalog') }}</div>
            <router-link class="menu-link" :class="{ 'is-current': isStandardProductsRoute }"
              to="/products/client" active-class="" @click="closeMenu">
              <vue-feather type="package" /> <span>{{ t('sidebar.productsAndPrices', 'Products and prices') }}</span>
            </router-link>
            <router-link v-if="hasIndividualProducts" class="menu-link"
              :class="{ 'is-current': isIndividualRoute }"
              :to="{ path: '/products/client', query: { type: 'individual' } }" active-class="" @click="closeMenu">
              <vue-feather type="tag" /> <span>{{ t('sidebar.partnerProducts', 'Partner products') }}</span>
            </router-link>

            <div class="menu-group-label">{{ t('sidebar.groups.orders', 'Orders') }}</div>
            <router-link v-if="canUseCart" class="menu-link" to="/admin/cart" @click="closeMenu">
              <vue-feather type="shopping-cart" /> <span>{{ t('sidebar.cart', 'Cart') }}</span>
              <span v-if="cartCount > 0" class="menu-badge">{{ compactCount(cartCount) }}</span>
            </router-link>
            <router-link class="menu-link" to="/orders/pending" @click="closeMenu">
              <vue-feather type="clipboard" /> <span>{{ t('sidebar.ordersPending', 'Pending approval') }}</span>
              <span v-if="pendingCount > 0" class="menu-badge">{{ compactCount(pendingCount) }}</span>
            </router-link>
            <router-link class="menu-link" to="/orders/placed" @click="closeMenu">
              <vue-feather type="shopping-bag" /> <span>{{ t('sidebar.ordersPlacedLong', 'Placed and in progress') }}</span>
            </router-link>
            <router-link class="menu-link" to="/admin_carts" @click="closeMenu">
              <vue-feather type="users" /> <span>{{ t('sidebar.activeCarts', 'Active carts') }}</span>
            </router-link>

            <div class="menu-group-label">{{ t('sidebar.groups.account', 'Account') }}</div>
            <router-link class="menu-link" to="/settings/employes" @click="closeMenu">
              <vue-feather type="key" /> <span>{{ t('sidebar.accountsAndAccess', 'Accounts and access') }}</span>
            </router-link>
            <router-link class="menu-link" to="/settings/client" @click="closeMenu">
              <vue-feather type="sliders" /> <span>{{ t('sidebar.viewSettings', 'Shop appearance') }}</span>
            </router-link>
            <router-link class="menu-link" to="/instructions" @click="closeMenu">
              <vue-feather type="help-circle" /> <span>{{ t('sidebar.instructions', 'User guide') }}</span>
            </router-link>

            <template v-if="hasRole('ADMIN')">
              <div class="menu-group-label">{{ t('sidebar.groups.administration', 'Administration') }}</div>
              <router-link class="menu-link" to="/settings/panel" @click="closeMenu">
                <vue-feather type="settings" /> <span>{{ t('sidebar.panelSettings', 'Panel settings') }}</span>
              </router-link>
              <router-link class="menu-link" to="/admin/catalog-settings" @click="closeMenu">
                <vue-feather type="filter" /> <span>{{ t('sidebar.catalogSettings', 'Catalog settings') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/users" @click="closeMenu">
                <vue-feather type="user-check" /> <span>{{ t('sidebar.users', 'Users') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/shops" @click="closeMenu">
                <vue-feather type="briefcase" /> <span>{{ t('sidebar.contractors', 'Partners') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/integrations" @click="closeMenu">
                <vue-feather type="link-2" /> <span>{{ t('sidebar.apiIntegrations', 'API integrations') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/integrationsXml" @click="closeMenu">
                <vue-feather type="database" /> <span>{{ t('sidebar.xmlIntegration', 'Product feeds') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/api-tokens" @click="closeMenu">
                <vue-feather type="terminal" /> <span>{{ t('sidebar.apiTokens', 'API tokens') }}</span>
              </router-link>
              <router-link class="menu-link" to="/settings/shippings" @click="closeMenu">
                <vue-feather type="truck" /> <span>{{ t('sidebar.shippingMethods', 'Shipping methods') }}</span>
              </router-link>
            </template>
          </nav>
        </div>

        <footer class="menu-footer">
          <label class="locale-field">
            <span>{{ t('sidebar.language', 'Language') }}</span>
            <select v-model="selectedLocale" :disabled="localesLoading" @change="onLocaleChange">
              <option v-for="option in orderedLocaleOptions" :key="option.value" :value="option.value">
                {{ languageDisplayName(option.value) }}
              </option>
            </select>
          </label>
          <div class="user-row">
            <span class="user-avatar">{{ userInitial }}</span>
            <div><strong>{{ userName }}</strong><small>{{ userEmail }}</small></div>
          </div>
          <button class="logout-button" type="button" @click="logout">
            {{ t('sidebar.logout', 'Log out') }} <vue-feather type="log-out" size="17" />
          </button>
        </footer>
      </aside>
    </div>
  </teleport>
</template>

<script>
import VueFeather from 'vue-feather'
import axios from 'axios'
import { applyLocaleForView, fetchLocaleOptions, FALLBACK, loadViewLocale, toBCP47 } from '@/i18n/index.js'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'Sidebar',
  components: { VueFeather },
  data() {
    return {
      menuOpen: false,
      languageMenuOpen: false,
      selectedLocale: localStorage.getItem('sv-lang') || FALLBACK,
      localeOptions: [],
      localesLoading: false,
      hasIndividualProducts: localStorage.getItem('hasIndividualProducts-B2B') === 'true',
      cartCount: 0,
      pendingCount: 0,
      shopCurrency: '',
      dashboardUserName: '',
      advisor: null,
      orderSearchQuery: '',
      orderSearchResults: [],
      orderSearchLoading: false,
      orderSearchOpen: false,
    }
  },
  computed: {
    auth() { return useAuthStore() },
    userEmail() { return this.auth.user?.email || '' },
    userName() { return this.dashboardUserName || this.userEmail },
    userInitial() { return (this.userName || 'U').trim().charAt(0).toUpperCase() },
    advisorInitial() { return (this.advisor?.name || 'P').trim().charAt(0).toUpperCase() },
    phoneHref() { return `tel:${String(this.advisor?.phone || '').replace(/[^+\d]/g, '')}` },
    selectedLanguageName() { return this.languageDisplayName(this.selectedLocale) },
    orderedLocaleOptions() {
      const order = ['pl', 'en', 'de', 'cs', 'sk', 'hu', 'ro', 'lt', 'fr', 'es', 'ru']
      return [...this.localeOptions].sort((a, b) => {
        const aIndex = order.indexOf(String(a.value).split('-')[0])
        const bIndex = order.indexOf(String(b.value).split('-')[0])
        return (aIndex < 0 ? 99 : aIndex) - (bIndex < 0 ? 99 : bIndex)
      })
    },
    isStandardProductsRoute() {
      return this.$route.path === '/products/client' && !this.isIndividualRoute
    },
    isIndividualRoute() {
      return this.$route.path === '/products/client'
        && String(this.$route.query?.type || '').toLowerCase() === 'individual'
    },
    canUseCart() {
      return this.auth.permissions?.canAddToCart !== false && this.auth.hasRole('ADMIN', 'PARTNER', 'KIEROWNIK')
    },
  },
  watch: {
    '$route.fullPath'() { this.closeMenu(); this.closeLanguageMenu(); this.closeOrderSearch() },
  },
  async mounted() {
    await applyLocaleForView('Sidebar', this.selectedLocale)
    await loadViewLocale('PartnerDashboard', this.selectedLocale)
    this.loadHeaderData()
    window.addEventListener('sv:cart-changed', this.onCartChanged)
    window.addEventListener('keydown', this.onGlobalKeydown)
    document.addEventListener('click', this.onDocumentClick)
    this.localesLoading = true
    try { this.localeOptions = await fetchLocaleOptions() } finally { this.localesLoading = false }
    const current = toBCP47(this.selectedLocale)
    if (!this.localeOptions.some(option => option.value === current)) {
      this.localeOptions.unshift({ value: current, label: current })
    }
    this.selectedLocale = current
  },
  beforeUnmount() {
    document.body.classList.remove('partner-menu-open')
    window.removeEventListener('sv:cart-changed', this.onCartChanged)
    window.removeEventListener('keydown', this.onGlobalKeydown)
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this._orderSearchTimer)
  },
  methods: {
    t(key, fallback) {
      try { const value = this.$t(key); return value === key ? fallback : value } catch { return fallback }
    },
    compactCount(value) { return Number(value) > 99 ? '99+' : String(value) },
    hasRole(...roles) { return this.auth.hasRole(...roles) },
    async loadHeaderData() {
      const calls = [axios.get('/api/admin/client-products/capabilities'), axios.get('/api/admin/dashboard')]
      if (this.canUseCart) calls.push(axios.get('/api/admin/my-cart/count'))
      const [capabilities, dashboard, cart] = await Promise.allSettled(calls)
      if (capabilities.status === 'fulfilled') {
        this.hasIndividualProducts = Boolean(capabilities.value.data?.hasIndividualProducts)
        localStorage.setItem('hasIndividualProducts-B2B', String(this.hasIndividualProducts))
      }
      if (dashboard.status === 'fulfilled') {
        this.pendingCount = Math.max(0, Number(dashboard.value.data?.pendingApprovalCount || 0))
        this.shopCurrency = dashboard.value.data?.currency || ''
        this.dashboardUserName = dashboard.value.data?.userName || ''
        this.advisor = dashboard.value.data?.advisor || null
      }
      if (cart?.status === 'fulfilled') this.cartCount = Math.max(0, Number(cart.value.data?.count || 0))
    },
    onCartChanged(event) {
      const count = Number(event?.detail?.count)
      if (Number.isFinite(count)) this.cartCount = Math.max(0, count)
      else if (this.canUseCart) axios.get('/api/admin/my-cart/count').then(({ data }) => {
        this.cartCount = Math.max(0, Number(data?.count || 0))
      }).catch(() => {})
    },
    openMenu() {
      this.menuOpen = true
      document.body.classList.add('partner-menu-open')
      this.$nextTick(() => this.$refs.closeButton?.focus())
    },
    closeMenu() {
      if (!this.menuOpen) return
      this.menuOpen = false
      document.body.classList.remove('partner-menu-open')
      this.$nextTick(() => this.$refs.menuButton?.focus())
    },
    onGlobalKeydown(event) {
      if (event.key !== 'Escape') return
      if (this.menuOpen) this.closeMenu()
      this.closeLanguageMenu()
      this.closeOrderSearch()
    },
    onDocumentClick(event) {
      if (this.languageMenuOpen && !this.$refs.languagePicker?.contains(event.target)) this.closeLanguageMenu()
      if (this.orderSearchOpen && !this.$refs.orderSearch?.contains(event.target)) this.closeOrderSearch()
    },
    openOrderSearch() { this.orderSearchOpen = true },
    closeOrderSearch() { this.orderSearchOpen = false },
    searchOrders() {
      clearTimeout(this._orderSearchTimer)
      this.orderSearchOpen = true
      const query = this.orderSearchQuery.trim()
      if (query.length < 2) {
        this.orderSearchResults = []
        this.orderSearchLoading = false
        return
      }
      this._orderSearchTimer = setTimeout(async () => {
        this.orderSearchLoading = true
        try {
          const { data } = await axios.get('/api/admin/orders/search', { params: { query, limit: 12 } })
          if (query === this.orderSearchQuery.trim()) this.orderSearchResults = Array.isArray(data) ? data : []
        } catch {
          this.orderSearchResults = []
        } finally {
          this.orderSearchLoading = false
        }
      }, 280)
    },
    orderSearchDescription(result) {
      const owner = result.customerName || result.employeeName || this.t('sidebar.orderSearch.noCustomer', 'Brak danych klienta')
      const number = result.shoperOrderId ? ` · #${result.shoperOrderId}` : ''
      return `${owner}${number}`
    },
    goToOrder(result) {
      this.closeOrderSearch()
      const search = result.confirmationNumber || result.shoperOrderId || String(result.id)
      if (result.type === 'PENDING') {
        this.$router.push({ path: '/orders/pending', query: { search, orderId: String(result.id) } })
      } else {
        const category = String(result.category || 'ACTIVE').toLowerCase()
        this.$router.push({ path: '/orders/placed', query: { category, search, orderId: String(result.id) } })
      }
      this.orderSearchQuery = ''
      this.orderSearchResults = []
    },
    toggleLanguageMenu() { this.languageMenuOpen = !this.languageMenuOpen },
    closeLanguageMenu() { this.languageMenuOpen = false },
    async selectLocale(locale) {
      this.selectedLocale = locale
      this.closeLanguageMenu()
      await this.onLocaleChange()
    },
    languageCode(locale) { return String(locale || '').split('-')[0].toUpperCase() },
    languageDisplayName(locale) {
      const names = {
        pl: 'Polski', en: 'English', de: 'Deutsch', cs: 'Čeština', sk: 'Slovenčina',
        hu: 'Magyar', ro: 'Română', lt: 'Lietuvių', fr: 'Français', es: 'Español', ru: 'Русский',
      }
      const code = String(locale || '').split('-')[0].toLowerCase()
      return names[code] || locale || '—'
    },
    onPanelKeydown(event) {
      if (event.key !== 'Tab') return
      const focusable = [...this.$refs.menuPanel.querySelectorAll('a,button,select,[tabindex]:not([tabindex="-1"])')]
        .filter(element => !element.disabled && element.offsetParent !== null)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    },
    async onLocaleChange() {
      const settings = useSettingsStore()
      await settings.changeLocale(toBCP47(this.selectedLocale))
      await Promise.all([
        loadViewLocale('Sidebar', settings.locale),
        loadViewLocale('PartnerDashboard', settings.locale),
      ])
      this.selectedLocale = settings.locale
      window.dispatchEvent(new CustomEvent('sv:locale-changed', { detail: { locale: settings.locale } }))
    },
    async logout() {
      await this.auth.logout()
      localStorage.removeItem('hasIndividualProducts-B2B')
      this.closeMenu()
      this.$router.push('/login')
    },
  },
}
</script>

<style>
body.partner-menu-open { overflow: hidden; }
</style>

<style scoped>
.panel-topbar { position: sticky; top: 0; z-index: 1020; height: 72px; background: #fff; border-bottom: 1px solid #e4e0db; }
.topbar-inner { height: 100%; max-width: 1440px; margin: 0 auto; padding: 0 28px; display: flex; align-items: center; gap: 10px; }
.topbar-button, .cart-button, .icon-button { min-height: 44px; border: 1px solid #ded9d3; border-radius: 11px; background: #fff; color: #20201e; display: inline-flex; align-items: center; justify-content: center; gap: 9px; padding: 0 14px; text-decoration: none; font: 600 14px/1 Inter, sans-serif; cursor: pointer; }
.topbar-button:hover, .icon-button:hover { background: #f5f2ef; color: #2a100a; }
.panel-logo { margin-left: 4px; color: #2a100a; text-decoration: none; font: 600 18px/1 "Clash Display", Inter, sans-serif; letter-spacing: .26em; white-space: nowrap; }
.global-order-search{position:relative;display:flex;flex:1;max-width:360px;align-items:center;gap:8px;margin-left:auto;padding:0 11px;border:1px solid #ded9d3;border-radius:11px;background:#fff;color:#77706a}
.global-order-search:focus-within{border-color:#b79468;box-shadow:0 0 0 3px rgba(183,148,104,.12)}
.global-order-search>input{width:100%;min-width:0;height:42px;border:0;outline:0;background:transparent;color:#20201e;font:500 13px Inter,sans-serif}
.global-search-spinner{width:15px;height:15px;flex:0 0 15px;border:2px solid #8a6a3f;border-right-color:transparent;border-radius:50%;animation:global-search-spin .7s linear infinite}
@keyframes global-search-spin{to{transform:rotate(360deg)}}
.global-search-results{position:absolute;top:calc(100% + 9px);left:0;right:0;z-index:50;min-width:320px;max-height:min(470px,calc(100vh - 100px));padding:6px;border:1px solid #ded9d3;border-radius:13px;background:#fff;box-shadow:0 18px 42px rgba(35,20,15,.18);overflow-y:auto}
.global-search-results button{width:100%;display:grid;grid-template-columns:34px minmax(0,1fr) 18px;align-items:center;gap:10px;padding:9px;border:0;border-radius:9px;background:transparent;color:#20201e;text-align:left;cursor:pointer}
.global-search-results button:hover,.global-search-results button:focus-visible{background:#f7f3ee;outline:0}.global-search-results button>span:nth-child(2){min-width:0}.global-search-results strong,.global-search-results small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.global-search-results strong{font-size:12px}.global-search-results small{margin-top:4px;color:#77706a;font-size:10px}.global-search-results button>svg{color:#8a6a3f}.global-search-icon{display:grid;width:32px;height:32px;place-items:center;border-radius:50%;background:#f3ece5;color:#2a100a}.global-search-empty{padding:18px 12px;color:#77706a;font-size:12px;text-align:center}
.topbar-actions { margin-left: 0; display: flex; align-items: center; gap: 10px; }
.currency-label { display: inline-flex; align-items: center; gap: 6px; font: 600 13px Inter, sans-serif; white-space: nowrap; }
.topbar-language-picker { position: relative; }
.topbar-language { min-height: 44px; padding: 0 34px 0 12px; border: 1px solid #ded9d3; border-radius: 11px; background: #fff; display: inline-flex; align-items: center; gap: 8px; color: #302d2a; font: 600 13px/1 Inter, sans-serif; cursor: pointer; }
.topbar-language:hover, .topbar-language[aria-expanded="true"] { background: #f7f4f0; border-color: #cfc7be; }
.topbar-language:disabled { cursor: wait; opacity: .65; }
.select-chevron { position: absolute; right: 10px; pointer-events: none; transition: transform .16s ease; }
.topbar-language[aria-expanded="true"] .select-chevron { transform: rotate(180deg); }
.language-menu { position: absolute; top: calc(100% + 10px); right: 0; z-index: 20; width: 220px; max-height: calc(100vh - 96px); padding: 6px; border: 1px solid #ded9d3; border-radius: 14px; background: #fff; box-shadow: 0 18px 42px rgba(35,20,15,.18); overflow-y: auto; }
.language-menu button { width: 100%; min-height: 50px; padding: 5px 11px; border: 0; border-radius: 10px; background: transparent; color: #20201e; display: flex; align-items: center; gap: 12px; font: 500 13px/1 Inter, sans-serif; text-align: left; cursor: pointer; }
.language-menu button:hover, .language-menu button:focus-visible { background: #f7f3ee; outline: 0; }
.language-menu button.selected { background: #f3eadc; font-weight: 700; }
.language-code { width: 34px; height: 34px; flex: 0 0 34px; border-radius: 50%; background: #f4f2ef; display: grid; place-items: center; font-size: 10px; font-weight: 700; }
.language-menu button.selected .language-code { background: #fff; }
.topbar-advisor { min-height: 44px; padding: 4px 5px 4px 7px; border: 1px solid #ded9d3; border-radius: 11px; background: #fff; display: flex; align-items: center; gap: 8px; }
.advisor-avatar { width: 32px; height: 32px; flex: 0 0 32px; border-radius: 50%; background: #2a100a; color: #fff; display: grid; place-items: center; font: 700 12px Inter, sans-serif; }
.advisor-copy small, .advisor-copy strong { display: block; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.advisor-copy small { color: #77706a; font: 500 9px/1.1 Inter, sans-serif; }
.advisor-copy strong { margin-top: 3px; font: 700 12px/1.1 Inter, sans-serif; }
.advisor-contact { min-height: 32px; padding: 0 8px; border-radius: 8px; color: #2a100a; display: inline-flex; align-items: center; justify-content: center; gap: 5px; text-decoration: none; font: 600 11px Inter, sans-serif; }
.advisor-phone { background: #2a100a; color: #fff; }
.advisor-email { width: 32px; padding: 0; border: 1px solid #ded9d3; }
.cart-button { position: relative; width: 58px; padding: 0; background: #2a100a; border-color: #2a100a; color: #fff; }
.cart-button:hover { color: #fff; }
.topbar-badge { position: absolute; top: -8px; right: -7px; min-width: 21px; height: 21px; padding: 0 5px; border-radius: 999px; background: #9b7b50; color: #fff; display: grid; place-items: center; font: 700 11px Inter, sans-serif; }
.menu-layer { position: fixed; inset: 0; z-index: 3000; }
.menu-backdrop { position: absolute; inset: 0; background: rgba(24, 14, 10, .58); backdrop-filter: blur(1px); }
.menu-panel { position: absolute; inset: 0 auto 0 0; width: min(390px, calc(100vw - 22px)); background: #fff; display: flex; flex-direction: column; box-shadow: 24px 0 50px rgba(35, 20, 15, .2); animation: menu-in .2s ease-out; color: #20201e; }
@keyframes menu-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
.menu-header { min-height: 78px; padding: 17px 22px; border-bottom: 1px solid #efebe6; display: flex; align-items: center; justify-content: space-between; }
.menu-logo { color: #2a100a; font: 600 19px/1 "Clash Display", Inter, sans-serif; letter-spacing: .25em; }
.menu-subtitle { margin-top: 6px; color: #77706a; font: 400 11px/1 Inter, sans-serif; }
.icon-button { width: 44px; padding: 0; border-color: transparent; }
.menu-scroll { flex: 1; overflow-y: auto; padding: 18px 14px 24px; }
.primary-action, .secondary-action { min-height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; text-decoration: none; font: 700 14px Inter, sans-serif; }
.primary-action { background: #2a100a; color: #fff; }
.secondary-action { margin-top: 8px; border: 1px solid #e1dcd7; color: #403b37; }
.menu-nav { margin-top: 17px; }
.menu-group-label { margin: 22px 10px 8px; color: #8a6a3f; font: 700 10px/1 Inter, sans-serif; letter-spacing: .22em; text-transform: uppercase; }
.menu-link { position: relative; min-height: 44px; padding: 0 12px; border-radius: 10px; display: flex; align-items: center; gap: 13px; color: #302d2a; text-decoration: none; font: 500 14px/1.25 Inter, sans-serif; }
.menu-link svg { width: 18px; height: 18px; flex: 0 0 18px; }
.menu-link:hover { background: #f7f3ef; color: #2a100a; }
.menu-link.router-link-active, .menu-link.is-current { background: #f3e9e3; color: #2a100a; font-weight: 700; }
.menu-badge { margin-left: auto; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px; background: #2a100a; color: #fff; display: grid; place-items: center; font-size: 11px; font-weight: 700; }
.menu-footer { padding: 16px 20px 20px; border-top: 1px solid #efebe6; background: #fff; }
.locale-field { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #77706a; font: 500 12px Inter, sans-serif; }
.locale-field select { max-width: 190px; min-height: 40px; border: 1px solid #ded9d3; border-radius: 10px; padding: 0 34px 0 11px; background: #fff; color: #302d2a; }
.user-row { margin-top: 14px; display: flex; align-items: center; gap: 11px; }
.user-row strong, .user-row small { display: block; max-width: 275px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-row strong { font: 700 13px Inter, sans-serif; }
.user-row small { margin-top: 3px; color: #77706a; font: 400 11px Inter, sans-serif; }
.user-avatar { width: 40px; height: 40px; border-radius: 50%; background: #2a100a; color: #fff; display: grid; place-items: center; font: 700 14px Inter, sans-serif; }
.logout-button { width: 100%; min-height: 44px; margin-top: 13px; border: 1px solid #ded9d3; border-radius: 11px; background: #fff; color: #302d2a; display: flex; align-items: center; justify-content: center; gap: 8px; font: 600 13px Inter, sans-serif; cursor: pointer; }
.topbar-button:focus-visible, .cart-button:focus-visible, .menu-link:focus-visible, .primary-action:focus-visible, .secondary-action:focus-visible, .logout-button:focus-visible, .locale-field select:focus-visible, .topbar-language:focus-visible, .advisor-contact:focus-visible, .icon-button:focus-visible { outline: 2px solid #8a6a3f; outline-offset: 2px; }
@media (max-width: 1180px) {
  .advisor-phone span, .advisor-email { display: none; }
  .advisor-contact { width: 32px; padding: 0; }
  .topbar-language > span { display: none; }
  .topbar-language { width: 44px; padding: 0; justify-content: center; }
  .topbar-language .select-chevron { display: none; }
}
@media (max-width: 930px) {
  .currency-label, .advisor-copy { display: none; }
  .global-order-search{max-width:220px}
}
@media (max-width: 760px) {
  .topbar-inner { padding: 0 12px; gap: 8px; }
  .desktop-label, .currency-label, .topbar-shop, .topbar-advisor { display: none; }
  .language-menu { right: -74px; }
  .topbar-button { width: 44px; padding: 0; }
  .panel-logo { margin: 0 auto; font-size: 14px; letter-spacing: .21em; }
  .global-order-search{flex:0 0 44px;width:44px;max-width:44px;margin-left:0;padding:0;justify-content:center}
  .global-order-search>input{position:absolute;inset:0;width:100%;opacity:0;cursor:pointer}
  .global-order-search:focus-within,.global-order-search.is-open{position:fixed;top:78px;left:12px;right:12px;z-index:1200;width:auto;max-width:none;padding:0 11px;justify-content:flex-start}
  .global-order-search:focus-within>input,.global-order-search.is-open>input{position:static;opacity:1;cursor:text}
  .global-search-results{min-width:0}
  .topbar-actions { margin-left: 0; gap: 8px; }
  .cart-button { width: 44px; }
}
</style>
